import {
  getFontCss,
  Popup,
  styleConstants,
  Tag,
  TagColorSchemes,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  forgetCognitoDevice,
  getCognitoTrustedDevices,
  getCurrentDeviceKey,
} from 'src/app/ManageTrustedDevices/redux/manageTrustedDevices.actions'
import {
  selectManageTrustedDevicesDevicesFromState,
  selectManageTrustedDevicesLoadingFromState,
} from 'src/app/ManageTrustedDevices/redux/manageTrustedDevices.selectors'
import AppName from 'src/components/AppName/AppName'
import IconButton from 'src/components/IconButton/IconButton'
import LoadingPopupContent from 'src/components/LoadingPopupContent/LoadingPopupContent'
import ObjectTable from 'src/components/ObjectTable/ObjectTable'
import ObjectTableBodyCell from 'src/components/ObjectTableBodyCell/ObjectTableBodyCell'
import ObjectTableBodyRow from 'src/components/ObjectTableBodyRow/ObjectTableBodyRow'
import SvgDesktop from 'src/components/Svgs/SvgDesktop'
import SvgPhone from 'src/components/Svgs/SvgPhone'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import SvgTrashHover from 'src/components/Svgs/SvgTrashHover'
import Trns from 'src/localization/Trns'
import { closeModal } from 'src/navigation/Lnk'
import {
  selectDeviceKeyFromState,
  selectFetchingCurrentDeviceKeyFromState,
} from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

const ManageTrustedDevices = (): React.ReactElement => {
  const loadingDevices = useSelector(selectManageTrustedDevicesLoadingFromState)
  const devices = useSelector(selectManageTrustedDevicesDevicesFromState)
  const fetchingDeviceKey = useSelector(selectFetchingCurrentDeviceKeyFromState)
  const deviceKey = useSelector(selectDeviceKeyFromState)

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getCurrentDeviceKey())
    dispatch(getCognitoTrustedDevices())
  }, [])

  // move current device element to top of array
  const currentDevice = devices?.filter((device) => device.id === deviceKey)[0]
  const currentDeviceIndex = currentDevice
    ? devices?.indexOf(currentDevice)
    : -1
  const sortedDevices = devices ? [...devices] : []

  if (currentDeviceIndex && currentDevice) {
    sortedDevices.splice(currentDeviceIndex, 1)
    sortedDevices.unshift(currentDevice)
  }

  return (
    <Popup
      showCloseButton
      onClickClose={(): void => {
        dispatch(closeModal())
      }}
    >
      {loadingDevices || fetchingDeviceKey ? (
        <LoadingPopupContent />
      ) : (
        <>
          {sortedDevices && sortedDevices.length !== 0 ? (
            <>
              <div
                css={css`
                  ${getFontCss(
                    32,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  margin-bottom: 20px;
                `}
              >
                <Trns source="Manage trusted devices" />
              </div>
              <div
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                  margin-bottom: 40px;
                `}
              >
                <Trns source="Manage which devices have been trusted. When logging in on a trusted device, you will not be required to enter a one-time password" />
              </div>
              <div
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  margin-bottom: 20px;
                `}
              >
                {sortedDevices?.length === 1 ? (
                  <Trns source="1 trusted device" />
                ) : (
                  <Trns
                    source="${num} trusted devices"
                    expressions={{
                      num: sortedDevices?.length.toString() || '0',
                    }}
                  />
                )}
              </div>
              <ObjectTable
                css={css`
                  background-color: ${styleConstants.colors.white};
                `}
              >
                {sortedDevices.map((device, i) => {
                  const rawName = device.name ?? device.id
                  const parsedName = (
                    device.name?.split(/[()]/)[1] || ''
                  ).replace(/;/g, '')
                  const name = parsedName.length > 0 ? parsedName : rawName
                  return (
                    <ObjectTableBodyRow
                      key={i}
                      ariaLabelSource={'Device ID'}
                      noLeftPadding
                    >
                      <ObjectTableBodyCell
                        noLeftPadding
                        css={css`
                          width: 90%;
                        `}
                      >
                        <div
                          css={css`
                            display: flex;
                            flex-direction: row;
                            ${deviceKey === device.id
                              ? css`
                                  align-items: flex-start;
                                `
                              : css`
                                  align-items: center;
                                `}
                          `}
                        >
                          <div
                            css={css`
                              margin-right: 15px;
                            `}
                          >
                            {parsedName.length > 0 ? (
                              <SvgDesktop
                                style={{
                                  transform: 'scale(1.5)',
                                  transformOrigin: 'top left',
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <SvgPhone
                                style={{
                                  transform: 'scale(1.5)',
                                  transformOrigin: 'top left',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </div>
                          <div
                            css={css`
                              display: flex;
                              flex-direction: column;
                            `}
                          >
                            <p
                              css={css`
                                ${getFontCss(
                                  12,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.regular,
                                )};
                              `}
                            >
                              {name}
                            </p>
                            {deviceKey === device.id && (
                              <div
                                css={css`
                                  margin-top: 4px;
                                `}
                              >
                                <Tag
                                  text={<Trns source="Current device" />}
                                  color={TagColorSchemes.Green}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </ObjectTableBodyCell>
                      <ObjectTableBodyCell>
                        <div
                          css={css`
                            cursor: pointer;
                            margin-left: 10px;
                            float: right;
                          `}
                        >
                          <IconButton
                            onClick={(): void => {
                              dispatch(forgetCognitoDevice(device))
                            }}
                            icon={<SvgTrash />}
                            hoverIcon={<SvgTrashHover />}
                          />
                        </div>
                      </ObjectTableBodyCell>
                    </ObjectTableBodyRow>
                  )
                })}
              </ObjectTable>
            </>
          ) : (
            <>
              <div
                css={css`
                  ${getFontCss(
                    32,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  margin-bottom: 20px;
                `}
              >
                <Trns source="No trusted devices" />
              </div>
              <div
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <Trns
                  source={
                    "To add a trusted device, you'll need to log into ${appName} on your device and check the option to 'Remember this device'. Once authenticated, your device will be added to this list."
                  }
                  expressions={{ appName: <AppName /> }}
                />
              </div>
            </>
          )}
        </>
      )}
    </Popup>
  )
}

export default ManageTrustedDevices
